
<template>
    <div class="rocket-container">
      <!-- 添加背景元素層 -->
      <div class="background-layer">
        <div v-for="(star, index) in stars" :key="index" 
             class="star" 
             :style="{ 
               left: star.x + '%', 
               top: star.y + '%', 
               opacity: star.opacity,
               width: star.size + 'px',
               height: star.size + 'px',
               backgroundColor: star.color,
               animationDuration: getStarAnimationDuration(star) + 's'
             }">
        </div>
      </div>
      
      <div ref="rocketWrapper" class="rocket-wrapper">
        <!-- 使用相對定位包裝火箭和火焰，確保它們始終連接在一起 -->
        <div class="rocket-flame-wrapper">
          <img ref="rocketImg" class="rocket-img" :src="rocketImage" alt="火箭" />
          
          <!-- 火焰效果放在火箭圖片後面，使火焰從火箭底部向下噴射 -->
          <div ref="flameContainer" class="flame-container">
            <svg class="flame-svg" :width="flameWidth" :height="flameHeight" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
              <defs>
                <radialGradient id="flameGradient" cx="50%" cy="30%" r="70%" fx="50%" fy="30%">
                  <stop offset="0%" stop-color="#ffff00" />
                  <stop offset="40%" stop-color="#ff9900" />
                  <stop offset="90%" stop-color="#ff0000" />
                  <stop offset="100%" stop-color="#cc0000" />
                </radialGradient>
              </defs>
              
              <!-- 主火焰 - 由上往下 -->
              <path :d="flamePath" fill="url(#flameGradient)">
                <animate attributeName="d" :values="flameAnimation" dur="0.5s" repeatCount="indefinite" />
              </path>
              
              <!-- 火焰內部 - 由上往下 -->
              <path :d="innerFlamePath" fill="#ffff99" opacity="0.7">
                <animate attributeName="d" :values="innerFlameAnimation" dur="0.7s" repeatCount="indefinite" />
              </path>
            </svg>
          </div>
        </div>
        
        <!-- 火力等級顯示 -->
        <div v-if="showPowerLevel" class="power-level">火力等級: {{ powerLevel }}</div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onMounted, reactive, watch, defineProps } from 'vue';
  
  // 定義 props
  const props = defineProps({
    // 從外部傳入的火力等級
    power: {
      type: Number,
      default: 0
    },
    // 是否顯示火力等級
    showPowerLevel: {
      type: Boolean,
      default: true
    },
    // 火箭圖片路徑
    rocketImage: {
      type: String,
      default: require('@/assets/HTwbActAssets/PvPTFgameObj/rocket.webp')
    },
    // 題目數量
    questionCount: {
      type: Number,
      default: 1
    }    
  });
  
  // 火焰容器元素引用
  const flameContainer = ref(null);
  
  // 火力等級
  const powerLevel = ref(props.power);
  const maxPowerLevel = 10;
  
  // 火焰尺寸 - 調整為向下生長，但寬度變化較小
  const baseFlameWidth = 70; // 更窄的基礎寬度
  const baseFlameHeight = 60;
  const flameWidth = computed(() => baseFlameWidth + (powerLevel.value * 4)); // 寬度增長更小
  const flameHeight = computed(() => baseFlameHeight + (powerLevel.value * 20)); // 高度增長更大
  
  // 火箭圖片參考
  const rocketImg = ref(null);
  
  // 背景星星設定
  const starCount = 30; // 星星數量
  const stars = reactive([]);
  
  // 根據火力等級計算星星動畫持續時間
  const getStarAnimationDuration = (star) => {
    // 基礎動畫時間
    const baseDuration = star.baseDuration;
    // 火力等級越高，動畫速度越快（持續時間越短）

    const speedFactor = Math.max(0.1, 1 - (powerLevel.value / maxPowerLevel) * 1.5);
    return baseDuration * speedFactor;
  };
  
  // 初始化星星
  const initStars = () => {
    stars.length = 0; // 清空現有星星
    for (let i = 0; i < starCount; i++) {
      stars.push({
        x: Math.random() * 100, // 隨機水平位置
        y: Math.random() * 100, // 隨機垂直位置
        size: Math.random() * 4 + 1, // 隨機大小 1-5px
        opacity: Math.random() * 0.7 + 0.3, // 隨機透明度 0.3-1.0
        baseDuration: Math.random() * 5 + 5, // 基礎動畫持續時間 5-10秒
        color: getRandomStarColor() // 隨機星星顏色，增加視覺效果
      });
    }
  };
  
  // 生成隨機星星顏色
  const getRandomStarColor = () => {
    // 星星顏色陣列 - 主要是白色和淺藍色系
    const colors = [
      '#ffffff', // 白色
      '#f0f8ff', // 愛麗絲藍
      '#e6e6fa', // 薰衣草色
      '#f0ffff', // 天藍色
      '#fffafa', // 雪白色
      '#f8f8ff', // 幽靈白
      '#f5f5f5'  // 白煙色
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  
  // 火焰路徑 - 修改為更瘦的形狀，尾端尖銳
  const flamePath = computed(() => {
    const intensity = Math.min(powerLevel.value / maxPowerLevel, 1);
    const height = 40 + (intensity * 40); // 增加高度增長
    // 火焰從頂部開始，向下擴展，尾端變尖，整體更瘦
    return `M35,0 H65 Q85,${20 + (8 * intensity)} 65,${height * 0.7} Q50,${height} 35,${height * 0.7} Q15,${20 + (8 * intensity)} 35,0 Z`;
  });
  
  const innerFlamePath = computed(() => {
    const intensity = Math.min(powerLevel.value / maxPowerLevel, 1);
    const height = 30 + (intensity * 35); // 增加高度增長
    // 內部火焰也從頂部開始，向下擴展，尾端變尖，整體更瘦
    return `M40,0 H60 Q75,${15 + (6 * intensity)} 60,${height * 0.7} Q50,${height} 40,${height * 0.7} Q25,${15 + (6 * intensity)} 40,0 Z`;
  });
  
  // 火焰動畫 - 調整為向下的動畫，保持尖端
  const flameAnimation = computed(() => {
    const intensity = Math.min(powerLevel.value / maxPowerLevel, 1);
    const height = 40 + (intensity * 40);
    const variation = 4 + (intensity * 4);
    
    return [
      `M35,0 H65 Q85,${20 + (8 * intensity)} 65,${height * 0.7} Q50,${height} 35,${height * 0.7} Q15,${20 + (8 * intensity)} 35,0 Z`,
      `M35,0 H65 Q85,${20 + (8 * intensity)} 65,${height * 0.7 - variation} Q50,${height - variation} 35,${height * 0.7 - variation} Q15,${20 + (8 * intensity)} 35,0 Z`,
      `M35,0 H65 Q85,${20 + (8 * intensity)} 65,${height * 0.7} Q50,${height} 35,${height * 0.7} Q15,${20 + (8 * intensity)} 35,0 Z`,
      `M35,0 H65 Q85,${20 + (8 * intensity)} 65,${height * 0.7 + variation} Q50,${height + variation} 35,${height * 0.7 + variation} Q15,${20 + (8 * intensity)} 35,0 Z`,
      `M35,0 H65 Q85,${20 + (8 * intensity)} 65,${height * 0.7} Q50,${height} 35,${height * 0.7} Q15,${20 + (8 * intensity)} 35,0 Z`
    ].join(';');
  });
  
  const innerFlameAnimation = computed(() => {
    const intensity = Math.min(powerLevel.value / maxPowerLevel, 1);
    const height = 30 + (intensity * 35);
    const variation = 2 + (intensity * 3);
    
    return [
      `M40,0 H60 Q75,${15 + (6 * intensity)} 60,${height * 0.7} Q50,${height} 40,${height * 0.7} Q25,${15 + (6 * intensity)} 40,0 Z`,
      `M40,0 H60 Q75,${15 + (6 * intensity)} 60,${height * 0.7 - variation} Q50,${height - variation} 40,${height * 0.7 - variation} Q25,${15 + (6 * intensity)} 40,0 Z`,
      `M40,0 H60 Q75,${15 + (6 * intensity)} 60,${height * 0.7} Q50,${height} 40,${height * 0.7} Q25,${15 + (6 * intensity)} 40,0 Z`,
      `M40,0 H60 Q75,${15 + (6 * intensity)} 60,${height * 0.7 + variation} Q50,${height + variation} 40,${height * 0.7 + variation} Q25,${15 + (6 * intensity)} 40,0 Z`,
      `M40,0 H60 Q75,${15 + (6 * intensity)} 60,${height * 0.7} Q50,${height} 40,${height * 0.7} Q25,${15 + (6 * intensity)} 40,0 Z`
    ].join(';');
  });
  
  // 監聽外部傳入的火力等級變化
  watch(() => props.power, () => {
    // 確保火力等級在有效範圍內
    powerLevel.value = props.power;
    updatePowerLevelCSS();
    updateBackgroundAnimation();
  });
  
  // 火箭元素引用
  const rocketWrapper = ref(null);

  // 更新火焰位置，確保它始終與火箭底部連接，並根據火力等級調整火箭位置
  const updatePowerLevelCSS = () => {
    // 使用公式計算火焰的 top 值
    // 當火力等級為 0 時，固定為 80px
    // 其他等級根據公式計算：80 - (powerLevel * 6)
    let topValue;
    
    if (powerLevel.value === 0) {
      topValue = 80;
    } else {
      // 根據觀察到的規律計算
      topValue = 80 - (powerLevel.value * 7.5);
      
      // 確保最小值不小於 10px，防止火焰完全超出火箭
      topValue = Math.max(topValue, 10);
    }
    
    // 使用 ref 獲取當前組件內部的火焰容器元素
    if (flameContainer.value) {
      // 設置火焰的 top 值
      flameContainer.value.style.top = topValue + 'px';
      
      // 輸出計算結果以方便調試
      console.log(`火力等級: ${powerLevel.value}, 火焰 top: ${topValue}px`);
    }
    
    // 根據火力等級調整火箭位置
    if (rocketWrapper.value) {
      // 取得父元素的高度
      const container = rocketWrapper.value.closest('.rocket-container');
      let containerHeight = 0;
      if (container) {
        containerHeight = container.clientHeight;
      }
      
      // 嘗試取得遊戲的題目數量
      let questionCount = props.questionCount; // 預設值
      try {
        // 如果在 HTpvpTFGamePlay 中，嘗試取得實際的題目數量
        const gameConfig = window.gameConfig || {};
        if (gameConfig.questionList && gameConfig.questionList.length > 0) {
          questionCount = gameConfig.questionList.length;
        }
      } catch (e) {
        console.log('無法取得題目數量，使用預設值');
      }
      
      // 計算每個火力等級應該上升的高度，使用容器高度除以題目數量
      const risePerLevel = containerHeight / questionCount;
      
      // 基礎位置 + 火力等級 * 每等級上升高度
      const bottomValue = -70 * window.innerHeight / 100 + 40 + (powerLevel.value * risePerLevel *0.64);
      rocketWrapper.value.style.bottom = `${bottomValue}px`;
      console.log(`火力等級: ${powerLevel.value}, 題目數: ${questionCount}, 每等級上升: ${risePerLevel}px, 火箭 bottom: ${bottomValue}px`);
    }
  };
  
  // 更新背景動畫效果
  const updateBackgroundAnimation = () => {
    const backgroundLayer = document.querySelector('.background-layer');
    if (backgroundLayer) {
      // 根據火力等級設置背景動畫速度
      const speedFactor = Math.min(1 + (powerLevel.value / maxPowerLevel) * 2, 3);
      backgroundLayer.style.setProperty('--speed-factor', speedFactor);
      
      // 移除背景模糊效果，保持背景清晰
      document.documentElement.style.setProperty('--star-blur', '0px');
      
      console.log(`火力等級: ${powerLevel.value}, 背景速度: ${speedFactor}x`);
    }
  };
  
  onMounted(() => {
    // 在組件掛載後初始化CSS變數
    console.log('火箭組件已掛載');
    initStars();
    
    
    // 初始化時設置火力等級
    powerLevel.value = props.power;
  });
  </script>
  
  <style scoped>
  .rocket-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden; /* 確保星星不會超出容器 */
    background-color: transparent; /* 透明背景，讓它可以融入遊戲界面 */
  }
  
  .title {
    font-size: 24px;
    margin-bottom: 20px;
    color: #ffffff; /* 在深色背景上使用白色文字 */
    z-index: 10; /* 確保標題在背景之上 */
  }
  
  .rocket-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 40px;
    bottom: calc(-70vh + 40px);
    transition: bottom 0.3s ease; /* 添加過渡效果使位置變化更平滑 */
  }
  
  /* 新增包裝元素，確保火箭和火焰始終連接在一起 */
  .rocket-flame-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .rocket-img {
    width: 120px;
    height: auto;
    z-index: 2;
    position: relative;
  }
  
  .flame-container {
    position: absolute;
    top: 80px; /* 初始定位在火箭底部，後續會由 JavaScript 動態調整 */
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    transform-origin: top center; /* 設定變形原點在頂部中心 */
    transition: top 0.3s ease; /* 添加過渡效果使位置變化更平滑 */
  }
  ;
  .flame-svg {
    display: block;
    margin-top: -1px; /* 微調火焰與火箭的連接點 */
  }
  
  .power-level {
    margin-top: 100px; /* 增加間距，因為火焰變長了 */
    font-size: 18px;
    font-weight: bold;
    color: #ff5722;
    z-index: 10; /* 確保在背景之上 */
    text-shadow: 0 0 5px rgba(255, 87, 34, 0.5); /* 添加發光效果 */
  }
  
  
  /* 背景層樣式 */
  .background-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    --speed-factor: 1; /* CSS變數，用於控制動畫速度 */
  }
  
  /* 星星樣式 */
  .star {
    position: absolute;
    border-radius: 50%;
    animation: starMove var(--star-duration, 8s) linear infinite;
    box-shadow: 0 0 2px 1px rgba(255, 255, 255, 0.3); /* 添加發光效果 */
  }
  
  /* 星星移動動畫 */
  @keyframes starMove {
    0% {
      transform: translateY(-5vh);
    }
    100% {
      transform: translateY(105vh) translateX(-20px); /* 稍微向左偏移，增強側向運動感 */
    }
  }
  
  /* 確保火箭和控制元素在背景之上 */
  .rocket-wrapper {
    position: relative;
    z-index: 10;
  }
  
  
  </style>